import { ExternalLink, Loader2, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { DocumentPreviewModal } from '@/components/documents/DocumentPreviewModal';
import { ApplicationTab } from '@/components/submission-detail/ApplicationTab';
import { DocumentsTab } from '@/components/submission-detail/DocumentsTab';
import { NotesTab } from '@/components/submission-detail/NotesTab';
import { OverviewTab } from '@/components/submission-detail/OverviewTab';
import { VideoTab } from '@/components/submission-detail/VideoTab';
import { WarningsBanner } from '@/components/submission-detail/WarningsBanner';
import { StatusBadge } from '@/components/submissions/StatusBadge';
import { computeSubmissionWarnings } from '@/lib/submissionWarnings';
import { cn } from '@/lib/utils';
import { getDocumentsForApplication } from '@/services/documentService';
import { getSubmissionDetail } from '@/services/submissionService';

import type { HrDocument } from '@/types/document.types';
import type { SubmissionDetail } from '@/types/submission.types';

type Tab = 'overview' | 'application' | 'documents' | 'video' | 'notes';

const TABS: Tab[] = ['overview', 'application', 'documents', 'video', 'notes'];

interface DetailDrawerProps {
  cardId: string | null;
  onClose: () => void;
  onBoardRefresh?: () => void;
}

export function DetailDrawer({ cardId, onClose, onBoardRefresh }: DetailDrawerProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const panelRef = useRef<HTMLDivElement>(null);

  const [detail, setDetail] = useState<SubmissionDetail | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [documents, setDocuments] = useState<HrDocument[]>([]);
  const [isVisible, setIsVisible] = useState(false);
  const [previewDocumentId, setPreviewDocumentId] = useState<string | null>(null);

  // Fetch detail when cardId changes
  useEffect(() => {
    if (!cardId) {
      setDetail(null);
      setIsVisible(false);
      return undefined;
    }

    setActiveTab('overview');
    setDocuments([]);
    setPreviewDocumentId(null);
    let isStale = false;

    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await getSubmissionDetail(cardId);
        if (!isStale) setDetail(data);
      } catch {
        if (!isStale) setError(t('board.drawer.errorLoading'));
      } finally {
        if (!isStale) setIsLoading(false);
      }
    };

    void load();
    return () => {
      isStale = true;
    };
  }, [cardId, t]);

  // Fetch documents for warnings computation when detail loads
  useEffect(() => {
    if (!detail) return;
    void getDocumentsForApplication(detail.id).then((docs) => {
      setDocuments(docs);
    });
  }, [detail]);

  const handleClose = useCallback(() => {
    setIsVisible(false);
    // Wait for slide-out transition to complete before calling onClose
    setTimeout(() => {
      onClose();
    }, 300);
  }, [onClose]);

  // Slide-in animation: set visible after mount/cardId change
  useEffect(() => {
    if (!cardId) return undefined;

    const frameId = requestAnimationFrame(() => {
      setIsVisible(true);
    });

    return () => cancelAnimationFrame(frameId);
  }, [cardId]);

  // Close on click outside
  useEffect(() => {
    if (!cardId) return undefined;

    const handleClickOutside = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        handleClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [cardId, handleClose]);

  // Close on Escape
  useEffect(() => {
    if (!cardId) return undefined;

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };

    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [cardId, handleClose]);

  const handleDocumentsLoaded = useCallback((docs: HrDocument[]) => {
    setDocuments(docs);
  }, []);

  const handleViewFull = useCallback(() => {
    if (cardId) {
      void navigate(`/submissions/${cardId}`);
    }
  }, [cardId, navigate]);

  const handlePreviewDocument = useCallback((docId: string) => {
    setPreviewDocumentId(docId);
  }, []);

  const handleClosePreview = useCallback(() => {
    setPreviewDocumentId(null);
  }, []);

  const handleDecisionMade = useCallback(async () => {
    if (!cardId) return;
    try {
      const data = await getSubmissionDetail(cardId);
      setDetail(data);
    } catch {
      // silently fail — detail will be stale but not broken
    }
    onBoardRefresh?.();
  }, [cardId, onBoardRefresh]);

  const warnings = useMemo(() => {
    if (!detail) return [];
    return computeSubmissionWarnings(detail, documents, t);
  }, [detail, documents, t]);

  if (!cardId) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className={cn(
          '!mt-0 fixed inset-0 z-40 bg-black/20 transition-opacity duration-300',
          isVisible ? 'opacity-100' : 'opacity-0',
        )}
      />

      {/* Panel */}
      <div
        ref={panelRef}
        className={cn(
          '!mt-0 fixed inset-y-0 right-0 z-50 flex w-full max-w-2xl flex-col bg-white shadow-xl border-l border-gray-200',
          'transition-transform duration-300 ease-in-out',
          isVisible ? 'translate-x-0' : 'translate-x-full',
        )}>
        {/* Header */}
        <div className='flex items-center justify-between border-b border-gray-200 px-6 py-4 shrink-0'>
          <div className='flex items-center gap-3 min-w-0'>
            {detail ? (
              <>
                <h2 className='text-lg font-semibold text-gray-900 truncate'>
                  {detail.firstName} {detail.lastName}
                </h2>
                <StatusBadge stage={detail.stage} />
              </>
            ) : (
              <h2 className='text-lg font-semibold text-gray-900'>{t('board.drawer.title')}</h2>
            )}
          </div>
          <button
            type='button'
            onClick={handleClose}
            className='rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition shrink-0'>
            <X size={20} />
          </button>
        </div>

        {/* Tab bar */}
        {detail && !isLoading ? (
          <div className='border-b border-gray-200 px-6 shrink-0'>
            <nav className='flex -mb-px space-x-4 overflow-x-auto'>
              {TABS.map((tab) => (
                <button
                  key={tab}
                  type='button'
                  onClick={() => setActiveTab(tab)}
                  className={cn(
                    'py-2 px-1 border-b-2 text-sm font-medium whitespace-nowrap transition-colors',
                    activeTab === tab
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
                  )}>
                  {t(`submissionDetail.tabs.${tab}`)}
                </button>
              ))}
            </nav>
          </div>
        ) : null}

        {/* Scrollable content area */}
        <div className='flex-1 overflow-y-auto'>
          {isLoading ? (
            <div className='flex items-center justify-center py-16'>
              <Loader2 size={24} className='animate-spin text-gray-400' />
            </div>
          ) : null}

          {error ? (
            <div className='px-6 py-4'>
              <div className='rounded-lg bg-red-50 border border-red-200 p-4 text-center'>
                <p className='text-sm text-red-700'>{error}</p>
              </div>
            </div>
          ) : null}

          {detail && !isLoading ? (
            <div className='px-6 py-4'>
              {/* Warnings banner */}
              {warnings.length > 0 ? (
                <div className='mb-4'>
                  <WarningsBanner warnings={warnings} onPreviewDocument={handlePreviewDocument} />
                </div>
              ) : null}

              {/* Tab content */}
              {activeTab === 'overview' ? (
                <OverviewTab detail={detail} onDecisionMade={() => void handleDecisionMade()} />
              ) : null}
              {activeTab === 'application' ? <ApplicationTab detail={detail} warnings={warnings} /> : null}
              {activeTab === 'documents' ? (
                <DocumentsTab
                  applicationId={detail.id}
                  onDocumentsLoaded={handleDocumentsLoaded}
                  applicationData={detail}
                />
              ) : null}
              {activeTab === 'video' ? <VideoTab video={detail.video} applicationId={detail.id} /> : null}
              {activeTab === 'notes' ? <NotesTab applicationId={detail.id} /> : null}
            </div>
          ) : null}
        </div>

        {/* Footer */}
        {detail && !isLoading ? (
          <div className='border-t border-gray-200 px-6 py-3 shrink-0'>
            <button
              type='button'
              onClick={handleViewFull}
              className='flex w-full items-center justify-center gap-2 rounded-lg bg-primary py-2.5 text-center text-sm font-medium text-white transition hover:bg-primary-dark'>
              <ExternalLink size={16} />
              {t('board.drawer.viewFullDetail')}
            </button>
          </div>
        ) : null}
      </div>

      {/* Document preview modal */}
      {previewDocumentId ? (
        <DocumentPreviewModal
          documentId={previewDocumentId}
          isOpen
          onClose={handleClosePreview}
          onStatusChange={() => {
            // Refresh handled by DocumentsTab
          }}
          applicationData={detail ?? undefined}
        />
      ) : null}
    </>
  );
}
